import { styled } from '@uhg-abyss/web/tools/styled';
import { Carousel, Slide } from '@uhg-abyss/web/ui/Carousel';
import { Text } from '@uhg-abyss/web/ui/Text';
import find from 'lodash/find';
import { useContext } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { CountySearchContext } from '../../context/CountySearchContext';
import { useLagoon } from '../../hooks/useLagoon';
import { useSmartChoice } from '../../hooks/useSmartChoice';
import { SnackCardProvider } from '../../hooks/useSnackCardProviderSearch/useSnackCardProviderSearch';
import { useSnackcardViewAllCount } from '../../hooks/useSnackcardViewAllCount';
import { useSuppressNiceMembers } from '../../hooks/useSuppressNiceMembers';
import { ResponseHeaders } from '../../models/ResponseHeaders';
import { useChipStore } from '../../store/useChipStore';
import { ChipState } from '../../store/useChipStore/chipStore';
import { returnSuppressFlag } from '../../utils/featureSuppress';
import {
  capitalizeFirstLetterOfEachWord,
  getMaxBadgeCount,
} from '../../utils/snackcard.utils';
import { getCurrentMember } from '../../utils/user.utils';
import { isVisionServiceSpeciality } from '../../utils/visionServicesOffered.utils';
import { CareCategories, ProviderTypes } from '../Constants';
import { ConstantsLagoon } from '../ConstantsLagoon';
import { OutsideCountyAlert } from '../DataCard/OutsideCountyAlert';
import { getGeoLocationFromStorage } from '../PSXHeader/SearchBar/utils';
import { CardInfo } from '../SnackCard/CardInfo';
import { SmartChoiceScoreCard } from '../SnackCard/SmartChoiceScoreCard';
import { SnackCard } from '../SnackCard/SnackCard';
import {
  SlideContentWrapper,
  carouselStyle,
  slideStyleSnack,
} from '../SnackCard/SnackCard.styles';
import { getFeatureFlag } from '../Utils';
import { makeAllSlidesAriaHidden } from '../Utils/a11yUtils/a11yUtils';
import { isMnrMember } from '../Utils/memberUtils/memberUtils';
import { SnackCardViewAllLink } from './SnackCardViewAllLink';
import { isOutOfCounty } from './utils';

export const PreferredBadgeWrapper = styled('div', {
  '@media (max-width: 464px)': {
    paddingBottom: '4px',
  },
});

type Props = {
  coverageType?: string;
  snackCardProviders: SnackCardProvider[];
  isLoading: boolean;
  isFixes: boolean;
  locationForAnalytics?: string;
  rulesPackageKey?: string;
  heading?: string;
  headers: ResponseHeaders;
  handleViewAllOnClick?: () => void;
  searchRadius?: number;
  isTieredPlan?: boolean;
  isHeadingAcronym?: boolean;
  recommendationPreferences?: any;
};

export const SnackCardCarousel = ({
  coverageType,
  snackCardProviders = [],
  isLoading,
  isFixes,
  locationForAnalytics,
  rulesPackageKey,
  headers,
  heading,
  handleViewAllOnClick,
  searchRadius,
  isTieredPlan = false,
  isHeadingAcronym = false,
  recommendationPreferences,
}: Props) => {
  const featureFlags = useLagoon('feature-flags')();
  const smartChoiceEligibility = useSmartChoice();
  const isSmartChoice = smartChoiceEligibility.isEnabled;
  const hasGreenDiamondPvdr = snackCardProviders.some(
    (obj) => obj?.isGreenDiamondProvider === true
  );
  const getLinkPositionIndex = (linkPosition?: number) => {
    if (linkPosition !== undefined) {
      return linkPosition + 1;
    }
    return linkPosition;
  };
  const countySearchAlertFlag: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_COUNTY_SEARCH_ALERT
  );
  const { isCountySearch } = useContext(CountySearchContext);
  const { name, stateCode } = getGeoLocationFromStorage();

  const noProvidersFoundData = useLagoon('ui-messaging')();
  const noProvidersFoundDataContent = find(noProvidersFoundData, {
    key: ConstantsLagoon.NO_PROVIDERS_FOUND,
  });
  const linkLocationName = `body:${locationForAnalytics}`;
  const { lineOfBusiness } = getCurrentMember();
  const isMNR = isMnrMember(lineOfBusiness);
  const { isExpandViewAllEnabled } = useSnackcardViewAllCount();
  const displayTerminationDateEnabled: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.DISPLAY_FUTURE_TERMINATION_DATE
  );

  // Get chip context for Smart Choice facility logic
  const chipStore = useChipStore(useShallow((state: ChipState) => state));
  const { chipValue } = chipStore;

  const hasSmartChoiceScoreCard =
    isSmartChoice &&
    snackCardProviders.some(
      (snackCardProvider) =>
        (snackCardProvider?.providerType?.toLocaleLowerCase() ===
          ProviderTypes.PRACTITIONER ||
          (snackCardProvider?.providerType?.toLocaleLowerCase() ===
            ProviderTypes.ORGANIZATION &&
            chipValue === CareCategories.FACILITIES)) &&
        parseInt(
          snackCardProvider?.recommendationDetails?.totalRecommendationScore ||
            '0'
        ) > 0
    );

  const isOutOfCntyIndicatorEnabled = snackCardProviders?.some(
    (snackCardProvider) =>
      isOutOfCounty(isCountySearch, name, stateCode, snackCardProvider)
  );

  const isPlatinumProviderIdentifierEnabled = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_PLATINUM_PROVIDER_IDENTIFIER
  );

  const suppressPremiumCare = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.PREMIUM_CARE
  );

  const { suppressForNiceMembers: suppressPremiumCareForNiceMembers } =
    useSuppressNiceMembers();

  const isMultipleBadgeCardViewEnabled: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_MULTIPLE_BADGE_DATA_CARD_VIEW
  );

  const maxBadgeCount = getMaxBadgeCount(
    snackCardProviders,
    isPlatinumProviderIdentifierEnabled,
    suppressPremiumCare || suppressPremiumCareForNiceMembers,
    isMultipleBadgeCardViewEnabled,
    displayTerminationDateEnabled
  );
  const enableVisionServicesOfferedSpecialityTransform = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS
      .ENABLE_VISION_SERVICES_OFFERED_SPECIALITY_TRANSFORM
  );

  const getSlides = () => {
    if (isLoading) {
      return [1, 2, 3, 4, 5].map((item) => (
        <SnackCard isFixes={isFixes} isLoading={isLoading} key={item} />
      ));
    }

    if (snackCardProviders.length) {
      return snackCardProviders.map((snackCardProvider, index) => {
        const isOnlineProvider =
          !!snackCardProvider?.website &&
          !snackCardProvider?.address?.line?.[0];

        const isVisionOrgOnlineProvider = isVisionServiceSpeciality(
          enableVisionServicesOfferedSpecialityTransform,
          isOnlineProvider,
          coverageType,
          snackCardProvider?.providerType,
          snackCardProvider?.servicesOffered
        );

        const speciality = !isVisionOrgOnlineProvider
          ? snackCardProvider?.speciality
          : capitalizeFirstLetterOfEachWord(
              snackCardProvider?.servicesOffered?.[0]?.replace(/_/g, ' ')
            );

        const isOutOfCnty = isOutOfCounty(
          isCountySearch,
          name,
          stateCode,
          snackCardProvider
        );
        return (
          <>
            <div key={`snack-card-carousel-${snackCardProvider.providerId}`}>
              {isSmartChoice &&
              (snackCardProvider?.providerType?.toLocaleLowerCase() ===
                ProviderTypes.PRACTITIONER ||
                (snackCardProvider?.providerType?.toLocaleLowerCase() ===
                  ProviderTypes.ORGANIZATION &&
                  chipValue === CareCategories.FACILITIES)) &&
              parseInt(
                snackCardProvider?.recommendationDetails
                  ?.totalRecommendationScore || '0'
              ) > 0 ? (
                <SmartChoiceScoreCard
                  recommendationDetails={
                    snackCardProvider?.recommendationDetails
                  }
                />
              ) : null}
              {!isSmartChoice && (
                <CardInfo
                  cardType="snack-card-carousel"
                  headers={headers}
                  indexForAnalytics={getLinkPositionIndex(index)}
                  landingPage
                  locationForAnalytics={linkLocationName}
                  practitioner={snackCardProvider}
                  rulesPackageKey={rulesPackageKey}
                />
              )}
              {hasGreenDiamondPvdr &&
                !snackCardProvider?.isGreenDiamondProvider &&
                !snackCardProvider?.isBccProvider && (
                  <div style={{ height: '25px' }}></div>
                )}
              <Slide css={slideStyleSnack}>
                <SlideContentWrapper>
                  <SnackCard
                    coverageType={coverageType}
                    distance={snackCardProvider.distance}
                    hasPhysicalAddress={!!snackCardProvider.address}
                    headers={headers}
                    imgSrc={snackCardProvider.imageLocation}
                    index={index}
                    isFixes={isFixes}
                    isLoading={isLoading}
                    isMNR={isMNR}
                    isSmartChoiceFFEnabled={isSmartChoice}
                    isTieredPlan={isTieredPlan}
                    isTieredProvider={snackCardProvider?.isTieredProvider}
                    isVirtualCareOffered={
                      !!snackCardProvider.virtualCareOffered?.length
                    }
                    key={snackCardProvider.locationId}
                    llmCodes={snackCardProvider?.llmCodes}
                    locationForAnalytics={locationForAnalytics}
                    locationId={snackCardProvider.locationId}
                    maxBadgeCount={maxBadgeCount}
                    networkEndDate={snackCardProvider?.networkEndDate}
                    networkStartDate={snackCardProvider?.networkStartDate}
                    organizationCode={snackCardProvider.organizationCode}
                    organizationType={snackCardProvider.organizationType}
                    professionalDesignation={
                      snackCardProvider.primaryDegrees?.length
                        ? snackCardProvider.primaryDegrees[0]
                        : ''
                    }
                    providerId={snackCardProvider.providerId}
                    providerName={snackCardProvider.providerName}
                    providerTierDesignation={
                      snackCardProvider?.recommendationDetails?.benefits
                        ?.providerTierDesignation
                    }
                    providerType={snackCardProvider.providerType}
                    rating={
                      snackCardProvider.healthGradeRating
                        ? parseFloat(
                            parseFloat(
                              snackCardProvider.healthGradeRating
                            ).toFixed(1)
                          )
                        : undefined
                    }
                    recommendationPreferences={recommendationPreferences}
                    rulesPackageKey={rulesPackageKey}
                    searchRadius={searchRadius}
                    snackCardProvider={snackCardProvider}
                    speciality={speciality}
                    virtualIndicator={snackCardProvider?.virtualIndicator || ''}
                    website={snackCardProvider?.website || ''}
                  />
                </SlideContentWrapper>
              </Slide>
              {(countySearchAlertFlag || isOutOfCntyIndicatorEnabled) && (
                <OutsideCountyAlert
                  county={name?.split(',')[0]}
                  isLandingPage
                  isOutOfCntyIndicatorEnabled={isOutOfCntyIndicatorEnabled}
                  isOutOfCounty={isOutOfCnty}
                />
              )}
            </div>
            {isExpandViewAllEnabled &&
              index === snackCardProviders.length - 1 &&
              handleViewAllOnClick && (
                <SnackCardViewAllLink
                  handleViewAllOnClick={handleViewAllOnClick}
                  hasSmartChoiceScoreCard={hasSmartChoiceScoreCard}
                  heading={heading}
                  isCaraousel={true}
                  isHeadingAcronym={isHeadingAcronym}
                  isOutOfCntyIndicatorEnabled={isOutOfCntyIndicatorEnabled}
                  key={index}
                  maxBadgeCount={maxBadgeCount}
                />
              )}
          </>
        );
      });
    }

    return (
      <Text
        color="#6E7072"
        css={{
          'abyss-text-root': {
            fontWeight: 500,
          },
        }}
      >
        {noProvidersFoundDataContent?.message ?? ''}
      </Text>
    );
  };

  makeAllSlidesAriaHidden();
  return (
    <Carousel
      css={carouselStyle}
      data-auto-testid="browse-facilities-carousel"
      data-testid="browse-facilities-carousel"
      label=""
      nextSlideOnClick={() => makeAllSlidesAriaHidden()}
      prevSlideOnClick={() => makeAllSlidesAriaHidden()}
      slideIndexOnClick={() => makeAllSlidesAriaHidden()}
      slides={getSlides()}
      slidesPerView={1}
    />
  );
};
